import React, { useState, useEffect } from 'react';
import {
    Save,
    Edit,
    Person,
    Email,
    Phone,
    Business,
    AdminPanelSettings,
    Logout,
    Close,
    CheckCircle,
    Badge
} from '@mui/icons-material';
import AdminSidebar from "../../components/admin/AdminSidebar";
import DashboardHeader from "../../components/admin/DashboardHeader";
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile } from '../../services/userRoleService';
import { updateOrganization, addOrganizationContact } from '../../services/organizationService';
import { hosts } from '../../config/environment';

// Style pour l'animation fadeIn
const fadeInStyle = `
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    .animate-fadeIn {
        animation: fadeIn 0.4s ease-out;
    }
`;

// Composant pour afficher une section d'information
const InfoSection = ({ title, icon: Icon, children }) => (
    <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-md">
                <Icon className="text-white text-xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        </div>
        <div className="space-y-1">
            {children}
        </div>
    </div>
);

// Composant pour afficher une information en lecture seule
const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="py-4 px-4 rounded-xl hover:bg-amber-50/50 transition-all duration-200 border-b border-gray-50 last:border-b-0 group">
        <div className="flex items-center space-x-2 mb-2">
            {Icon && <Icon className="text-amber-500 text-base group-hover:text-amber-600 transition-colors" />}
            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
        </div>
        <p className="text-lg font-bold text-gray-900 ml-7">{value || <span className="text-gray-400 italic">Non renseigné</span>}</p>
    </div>
);

// Composant pour un champ de saisie
const InputField = ({ label, value, onChange, type = 'text', placeholder }) => (
    <div className="py-4">
        <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-2 block">{label}</span>
            <input
                type={type}
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white hover:border-amber-300"
            />
        </label>
    </div>
);

// Composant pour les boutons radio (sexe)
const RadioButton = ({ label, value, selected, onChange }) => (
    <button
        type="button"
        onClick={() => onChange(value)}
        className={`flex items-center space-x-3 px-6 py-3 rounded-xl border-2 transition-all duration-200 ${
            selected === value
                ? 'border-amber-500 bg-amber-50 shadow-md'
                : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/30'
        }`}
    >
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            selected === value
                ? 'border-amber-500 bg-amber-500'
                : 'border-gray-300'
        }`}>
            {selected === value && (
                <div className="w-3 h-3 rounded-full bg-white animate-pulse"></div>
            )}
        </div>
        <span className={`text-base font-medium ${
            selected === value ? 'text-amber-700' : 'text-gray-700'
        }`}>{label}</span>
    </button>
);

// URL avatar utilisateur via API (comme sur mobile)
const getAvatarUrl = (userId) => (userId && hosts.api)
    ? `${hosts.api}/exposed/v1/mda/images/show?target=avatar&target_id=${userId}`
    : null;

// URL logo organisation via API (comme sur mobile)
const getOrgLogoUrl = (orgId) => (orgId && hosts.api)
    ? `${hosts.api}/exposed/v1/mda/images/show?target=logo&target_id=${orgId}`
    : '';

// Afficher le libellé du statut (active, suspended, etc.)
const formatStatusLabel = (status) => {
    if (status === null || status === undefined) return '—';
    const s = String(status).toLowerCase();
    if (s === 'active' || s === 'actif') return 'Actif';
    if (s === 'suspended' || s === 'suspendu') return 'Suspendu';
    if (s === 'inactive' || s === 'inactif') return 'Inactif';
    return status;
};

// Afficher les rôles (current_user_roles ou role)
const formatRolesLabel = (org) => {
    if (!org) return '—';
    const roles = org.current_user_roles || org.roles;
    if (Array.isArray(roles) && roles.length > 0) {
        const names = roles.map((r) => (typeof r === 'object' && r?.name ? r.name : String(r)));
        return names.join(', ');
    }
    if (org.role) return org.role === 'both' ? 'Admin, Caissier' : org.role;
    return '—';
};

// Récupérer le numéro de téléphone de l'organisation (contact API: fullNumber, ou zip + phone)
const getOrganizationPhone = (contact) => {
    if (!contact) return '';
    if (contact.fullNumber) return contact.fullNumber;
    const zip = contact.zip != null ? String(contact.zip).replace(/\D/g, '') : '';
    const phone = contact.phone != null ? String(contact.phone).trim() : '';
    if (zip && phone) return `+${zip} ${phone}`;
    if (phone) return phone;
    return '';
};

// Composant pour l'avatar
const AvatarDisplay = ({ user }) => {
    const firstName = user?.profile?.firstName || 'N';
    const lastName = user?.profile?.lastName || 'K';
    const avatarUrl = getAvatarUrl(user?.id)
        || `https://ui-avatars.com/api/?name=${encodeURIComponent(firstName + ' ' + lastName)}&background=f59e0b&color=fff`;
    
    return (
        <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
            <div className="relative">
                <img
                    src={avatarUrl}
                    alt="Avatar"
                    className="w-28 h-28 rounded-full border-4 border-white shadow-2xl ring-4 ring-amber-100 object-cover"
                    onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(firstName + ' ' + lastName)}&background=f59e0b&color=fff`;
                    }}
                />
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
            </div>
        </div>
    );
};

// Valeurs par défaut pour le formulaire organisation
const defaultOrgForm = () => ({
    name: '',
    description: '',
    category_id: '',
    category_name: '',
    rccm: '',
    nif: '',
    idnat: '',
    contactPhone: '',
    contactEmail: '',
    contactWebsite: ''
});

const Settings = () => {
    const { user, selectedOrganization, logout } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [openEditForm, setOpenEditForm] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // État du profil (comme sur mobile)
    const [profile, setProfile] = useState({
        firstName: user?.profile?.firstName || '',
        lastName: user?.profile?.lastName || '',
        gender: user?.profile?.gender || 'MALE',
        completeName: user?.profile?.completeName || ''
    });

    // Paramètres de l'organisation
    const [orgForm, setOrgForm] = useState(defaultOrgForm);
    const [orgEditMode, setOrgEditMode] = useState(false);
    const [savingOrg, setSavingOrg] = useState(false);
    const [orgLoadError, setOrgLoadError] = useState(null);

    // Mettre à jour le profil quand l'utilisateur change
    useEffect(() => {
        if (user?.profile) {
            setProfile({
                firstName: user.profile.firstName || '',
                lastName: user.profile.lastName || '',
                gender: user.profile.gender || 'MALE',
                completeName: user.profile.completeName || ''
            });
        }
    }, [user]);

    // Charger / synchroniser les données organisation depuis selectedOrganization ou API
    useEffect(() => {
        if (!selectedOrganization) {
            setOrgForm(defaultOrgForm());
            return;
        }
        const contact = selectedOrganization?.contact || {};
        setOrgForm({
            name: selectedOrganization.name ?? '',
            description: selectedOrganization.description ?? '',
            category_id: selectedOrganization.category_id ?? selectedOrganization.category?.id ?? '',
            category_name: selectedOrganization.category_name ?? selectedOrganization.category?.name ?? '',
            rccm: selectedOrganization.rccm ?? '',
            nif: selectedOrganization.nif ?? '',
            idnat: selectedOrganization.idnat ?? '',
            contactPhone: getOrganizationPhone(contact),
            contactEmail: contact.email ?? '',
            contactWebsite: contact.official_web_site ?? ''
        });
    }, [selectedOrganization]);

    // Mettre à jour completeName quand firstName ou lastName change
    useEffect(() => {
        const completeName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
        setProfile(prev => ({...prev, completeName}));
    }, [profile.firstName, profile.lastName]);

    const handleSaveProfile = async () => {
        try {
            setSaving(true);
            const response = await updateUserProfile(user.id, profile);
            if (response) {
                alert('Profil mis à jour avec succès !');
                setOpenEditForm(false);
                // Recharger les données utilisateur si nécessaire
                window.location.reload(); // Simple refresh pour recharger les données
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            alert(error.message || 'Erreur lors de la mise à jour du profil');
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = () => {
        if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
            logout();
        }
    };

    const handleSaveOrganization = async () => {
        if (!selectedOrganization?.id) return;
        setOrgLoadError(null);
        setSavingOrg(true);
        try {
            await updateOrganization(selectedOrganization.id, {
                name: orgForm.name,
                description: orgForm.description || undefined,
                rccm: orgForm.rccm || undefined,
                nif: orgForm.nif || undefined,
                idnat: orgForm.idnat || undefined,
                category_id: orgForm.category_id || selectedOrganization.category_id || selectedOrganization.category?.id
            });
            const phoneOnly = (orgForm.contactPhone || '').replace(/\D/g, '').slice(-9) || orgForm.contactPhone;
            await addOrganizationContact(selectedOrganization.id, {
                zip: '243',
                phone: phoneOnly || orgForm.contactPhone || '',
                email: orgForm.contactEmail || '',
                official_web_site: orgForm.contactWebsite || ''
            });
            setOrgEditMode(false);
            alert('Paramètres de l\'organisation enregistrés.');
        } catch (err) {
            console.error(err);
            setOrgLoadError(err.message || 'Erreur lors de l\'enregistrement');
            alert(err.message || 'Erreur lors de l\'enregistrement des paramètres.');
        } finally {
            setSavingOrg(false);
        }
    };

    return (
        <>
            <style>{fadeInStyle}</style>
            <div className="min-h-screen bg-white flex">
                <AdminSidebar
                    isOpen={sidebarOpen}
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    activeSection="settings"
                />

                <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                    <div className="bg-gradient-to-br from-gray-50 via-white to-amber-50/30 min-h-screen py-6 pr-6 pl-0">
                        <div className="relative z-10 space-y-6 pl-6">
                        <DashboardHeader
                            timeRange={timeRange}
                            onTimeRangeChange={setTimeRange}
                            currency={currency}
                            onCurrencyChange={setCurrency}
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        />
                        <div className="space-y-6">
                            {/* Titre de page — charte amber */}
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
                                    <Business className="text-white text-xl" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-gray-900">Paramètres de l'organisation</h1>
                                    <p className="text-sm text-gray-500">Informations et contact de votre organisation</p>
                                </div>
                            </div>

                            {/* Paramètres de l'organisation — page profil, charte graphique */}
                            {selectedOrganization && (
                                <div className="bg-white rounded-3xl border border-amber-100/80 shadow-xl shadow-amber-900/5 overflow-hidden">
                                    {/* En-tête type profil — dégradé charte amber/orange */}
                                    <div className="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 px-8 pt-8 pb-24 text-white relative overflow-hidden">
                                        <div className="absolute inset-0 bg-black/5" />
                                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
                                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />
                                        <div className="relative z-10 flex flex-wrap items-end gap-6">
                                            <div className="flex items-center gap-5">
                                                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center overflow-hidden border border-white/30 flex-shrink-0 shadow-lg ring-2 ring-white/20">
                                                    {getOrgLogoUrl(selectedOrganization.id) ? (
                                                        <img
                                                            src={getOrgLogoUrl(selectedOrganization.id)}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const fallback = e.target.nextElementSibling;
                                                                if (fallback) fallback.classList.remove('hidden');
                                                            }}
                                                        />
                                                    ) : null}
                                                    <span className={`text-3xl font-bold text-white ${getOrgLogoUrl(selectedOrganization.id) ? 'hidden' : ''}`}>
                                                        {(orgForm.name || selectedOrganization.name || 'O').charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <h2 className="text-2xl font-bold tracking-tight text-white drop-shadow-sm">
                                                        {orgForm.name || selectedOrganization.name || 'Organisation'}
                                                    </h2>
                                                    <p className="text-amber-100 text-sm mt-0.5">
                                                        {orgForm.category_name || selectedOrganization.category?.name || selectedOrganization.category || '—'}
                                                    </p>
                                                </div>
                                            </div>
                                            {!orgEditMode ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setOrgEditMode(true)}
                                                    className="ml-auto flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/20 hover:bg-white/30 border border-white/30 transition-all font-medium shadow-sm"
                                                >
                                                    <Edit className="text-lg" />
                                                    <span>Modifier</span>
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="px-8 pb-8 -mt-16 relative z-10">
                                        {orgLoadError && (
                                            <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200">
                                                {orgLoadError}
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                            {/* Colonne principale */}
                                            <div className="lg:col-span-2 space-y-6">
                                                <div className="bg-amber-50/60 rounded-2xl p-6 border border-amber-100 hover:shadow-md transition-shadow duration-200">
                                                    <h3 className="flex items-center gap-2 text-gray-900 font-semibold mb-4">
                                                        <span className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center">
                                                            <Business className="text-amber-600 text-lg" />
                                                        </span>
                                                        Informations générales
                                                    </h3>
                                                    {orgEditMode ? (
                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-1">Dénomination / Nom</label>
                                                                <input
                                                                    type="text"
                                                                    value={orgForm.name}
                                                                    onChange={(e) => setOrgForm((p) => ({ ...p, name: e.target.value }))}
                                                                    className="w-full px-4 py-2.5 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white placeholder:text-gray-400"
                                                                    placeholder="Nom de l'organisation"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                                                                <textarea
                                                                    value={orgForm.description}
                                                                    onChange={(e) => setOrgForm((p) => ({ ...p, description: e.target.value }))}
                                                                    rows={2}
                                                                    className="w-full px-4 py-2.5 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white resize-none placeholder:text-gray-400"
                                                                    placeholder="Brève description (optionnel)"
                                                                />
                                                            </div>
                                                            <p className="text-xs text-amber-700/80">Catégorie : {orgForm.category_name || selectedOrganization?.category?.name || '—'} (lecture seule)</p>
                                                            <div>
                                                                <span className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Statut</span>
                                                                <p className="text-gray-700 mt-1">{formatStatusLabel(selectedOrganization?.status)}</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Rôles</span>
                                                                <p className="text-gray-700 mt-1">{formatRolesLabel(selectedOrganization)}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <dl className="space-y-3">
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Dénomination</dt>
                                                                <dd className="text-gray-900 font-medium mt-0.5">{orgForm.name || '—'}</dd>
                                                            </div>
                                                            {orgForm.description && (
                                                                <div>
                                                                    <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Description</dt>
                                                                    <dd className="text-gray-700 mt-0.5">{orgForm.description}</dd>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Catégorie</dt>
                                                                <dd className="text-gray-700 mt-0.5">{orgForm.category_name || selectedOrganization?.category?.name || '—'}</dd>
                                                            </div>
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Statut</dt>
                                                                <dd className="mt-0.5">
                                                                    {(() => {
                                                                        const label = formatStatusLabel(selectedOrganization?.status);
                                                                        if (label === '—') return <span className="text-gray-400 italic">Non renseigné</span>;
                                                                        const isActive = label === 'Actif';
                                                                        return (
                                                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium ${isActive ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                                                                                {label}
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </dd>
                                                            </div>
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Rôles</dt>
                                                                <dd className="text-gray-700 mt-0.5">{formatRolesLabel(selectedOrganization)}</dd>
                                                            </div>
                                                        </dl>
                                                    )}
                                                </div>

                                                <div className="bg-amber-50/60 rounded-2xl p-6 border border-amber-100 hover:shadow-md transition-shadow duration-200">
                                                    <h3 className="flex items-center gap-2 text-gray-900 font-semibold mb-4">
                                                        <span className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center">
                                                            <Badge className="text-amber-600 text-lg" />
                                                        </span>
                                                        Identifiants légaux
                                                    </h3>
                                                    {orgEditMode ? (
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-1">RCCM</label>
                                                                <input
                                                                    type="text"
                                                                    value={orgForm.rccm}
                                                                    onChange={(e) => setOrgForm((p) => ({ ...p, rccm: e.target.value }))}
                                                                    className="w-full px-4 py-2.5 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white placeholder:text-gray-400"
                                                                    placeholder="Ex: CD/KIN/RCCM/…"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-1">NIF</label>
                                                                <input
                                                                    type="text"
                                                                    value={orgForm.nif}
                                                                    onChange={(e) => setOrgForm((p) => ({ ...p, nif: e.target.value }))}
                                                                    className="w-full px-4 py-2.5 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white placeholder:text-gray-400"
                                                                    placeholder="Numéro NIF"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-1">IDNAT</label>
                                                                <input
                                                                    type="text"
                                                                    value={orgForm.idnat}
                                                                    onChange={(e) => setOrgForm((p) => ({ ...p, idnat: e.target.value }))}
                                                                    className="w-full px-4 py-2.5 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white placeholder:text-gray-400"
                                                                    placeholder="Identifiant national"
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">RCCM</dt>
                                                                <dd className="text-gray-900 font-medium mt-1">{orgForm.rccm || '—'}</dd>
                                                            </div>
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">NIF</dt>
                                                                <dd className="text-gray-900 font-medium mt-1">{orgForm.nif || '—'}</dd>
                                                            </div>
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">IDNAT</dt>
                                                                <dd className="text-gray-900 font-medium mt-1">{orgForm.idnat || '—'}</dd>
                                                            </div>
                                                        </dl>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Colonne contact — numéro de l'organisation */}
                                            <div className="space-y-6">
                                                <div className="bg-amber-50/60 rounded-2xl p-6 border border-amber-100 hover:shadow-md transition-shadow duration-200">
                                                    <h3 className="flex items-center gap-2 text-gray-900 font-semibold mb-4">
                                                        <span className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center">
                                                            <Phone className="text-amber-600 text-lg" />
                                                        </span>
                                                        Contact de l'organisation
                                                    </h3>
                                                    {orgEditMode ? (
                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-1">Téléphone de l'organisation</label>
                                                                <input
                                                                    type="text"
                                                                    value={orgForm.contactPhone}
                                                                    onChange={(e) => setOrgForm((p) => ({ ...p, contactPhone: e.target.value }))}
                                                                    className="w-full px-4 py-2.5 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white placeholder:text-gray-400"
                                                                    placeholder="+243 XXX XXX XXX"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-1">E-mail</label>
                                                                <input
                                                                    type="email"
                                                                    value={orgForm.contactEmail}
                                                                    onChange={(e) => setOrgForm((p) => ({ ...p, contactEmail: e.target.value }))}
                                                                    className="w-full px-4 py-2.5 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white placeholder:text-gray-400"
                                                                    placeholder="contact@organisation.com"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-1">Site web</label>
                                                                <input
                                                                    type="url"
                                                                    value={orgForm.contactWebsite}
                                                                    onChange={(e) => setOrgForm((p) => ({ ...p, contactWebsite: e.target.value }))}
                                                                    className="w-full px-4 py-2.5 rounded-xl border border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white placeholder:text-gray-400"
                                                                    placeholder="https://…"
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <dl className="space-y-4">
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Téléphone</dt>
                                                                <dd className="text-gray-900 font-medium mt-1">{orgForm.contactPhone || '—'}</dd>
                                                                <p className="text-xs text-gray-500 mt-0.5">Numéro de contact de l'organisation</p>
                                                            </div>
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">E-mail</dt>
                                                                <dd className="text-gray-900 font-medium mt-1">{orgForm.contactEmail || '—'}</dd>
                                                            </div>
                                                            <div>
                                                                <dt className="text-xs font-medium text-amber-700/80 uppercase tracking-wide">Site web</dt>
                                                                <dd className="text-gray-900 font-medium mt-1 break-all">{orgForm.contactWebsite || '—'}</dd>
                                                            </div>
                                                        </dl>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {orgEditMode && (
                                            <div className="flex flex-wrap items-center gap-3 mt-6 pt-6 border-t border-amber-100">
                                                <button
                                                    type="button"
                                                    onClick={handleSaveOrganization}
                                                    disabled={savingOrg}
                                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-medium shadow-md shadow-amber-900/20 hover:shadow-lg transition-all disabled:opacity-60"
                                                >
                                                    {savingOrg ? (
                                                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <Save className="text-lg" />
                                                    )}
                                                    <span>{savingOrg ? 'Enregistrement…' : 'Enregistrer les paramètres'}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setOrgEditMode(false); setOrgLoadError(null); }}
                                                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all border border-gray-200"
                                                >
                                                    Annuler
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Section Rôle Actuel - À implémenter si disponible dans le contexte */}
                            {/* {user?.currentRole && (
                                <InfoSection title="Rôle Actuel" icon={AdminPanelSettings}>
                                    <InfoRow
                                        label="Rôle"
                                        value={user.currentRole?.name}
                                    />
                                </InfoSection>
                            )} */}

                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Settings;